import { useEffect, useRef, useState, useCallback } from "react";
import { Helmet } from "react-helmet";
import { DateRangePicker } from "react-date-range";
import moment from "moment";
import DataTable from "react-data-table-component";

/* MATERIAL TAILWIND COMPONENTS */
import { Button, Tooltip } from "@material-tailwind/react";

/* ICONS */
import { FaCalendar } from "react-icons/fa6";
import { IoEyeSharp, IoClose, IoSyncOutline } from "react-icons/io5";

/* USER DEFINED COMPONENTS */
import InputField from "../../../components/input";
import { getOrderListAPI, syncOrderRazorpayAPI } from "../../../service/api/order.api";
import { SET_FORMAT } from "../../../config";
import { useNavigate } from "react-router-dom";
import { handleAddToken } from "../../../service/request";
import { useSelector } from "react-redux";
import { toast } from "react-toast";


/* CLICK OUTSIDE HANDLER */
const useClickOutside = (handler) => {
  let domNode = useRef();

  useEffect(() => {
    let maybeHandler = (event) => {
      if (domNode.current && !domNode.current.contains(event.target)) {
        handler();
      }
    };

    document.addEventListener("mousedown", maybeHandler);
    return () => {
      document.removeEventListener("mousedown", maybeHandler);
    };
  }, [handler]);

  return domNode;
};

const Orders = () => {
  const navigate = useNavigate();

  // GLOBAL VARIABLES
  const userState = useSelector((state) => state?.user);

  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(10);
  const [ordersList, setOrdersList] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [syncingId, setSyncingId] = useState(null);

  /* DATEPICKER VARIABLES */
  const [datepicker, setDatepicker] = useState(false);
  const [isDateFiltered, setIsDateFiltered] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: moment().subtract(1, "month").toDate(),
      endDate: new Date(),
      key: "selection",
    },
  ]);
  const [tempRange, setTempRange] = useState([
    {
      startDate: moment().subtract(1, "month").toDate(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const closeDatePicker = useCallback(() => {
    setDatepicker(false);
  }, []);

  const domNode = useClickOutside(closeDatePicker);

  const handleOpenDatePicker = () => {
    setTempRange([
      {
        startDate: dateRange[0]?.startDate || moment().subtract(1, "month").toDate(),
        endDate: dateRange[0]?.endDate || new Date(),
        key: "selection",
      },
    ]);
    setDatepicker((prev) => !prev);
  };

  const handleApplyDate = () => {
    setDateRange(tempRange);
    setIsDateFiltered(true);
    setDatepicker(false);
    setPage(1);
  };

  const handleClearDate = () => {
    setIsDateFiltered(false);
    setDatepicker(false);
    setPage(1);
  };

  // Fetch orders function
  const ordersGetFunction = useCallback(async () => {
    const obj = {
      page,
      sizePerPage,
      pagination: true,
    };
    if (search && search.trim()) {
      obj.search = search.trim();
    }
    if (selectedStatus) {
      obj.status = selectedStatus?.value;
    }
    if (isDateFiltered && dateRange[0]?.startDate && dateRange[0]?.endDate) {
      obj.startDate = moment(dateRange[0].startDate).startOf("day").toISOString();
      obj.endDate = moment(dateRange[0].endDate).endOf("day").toISOString();
    }

    await handleAddToken(userState?.token);
    setLoading(true);
    getOrderListAPI(obj)
      .then((response) => {
        if (response?.status) {
          let docs = response?.data?.docs || [];

          // Client-side fallback filter in case server only checks end date
          if (isDateFiltered && dateRange[0]?.startDate && dateRange[0]?.endDate) {
            const start = moment(dateRange[0].startDate).startOf("day");
            const end = moment(dateRange[0].endDate).endOf("day");
            docs = docs.filter((item) => {
              if (!item?.createdAt) return false;
              const itemDate = moment(item.createdAt);
              return itemDate.isSameOrAfter(start) && itemDate.isSameOrBefore(end);
            });
          }

          setOrdersList(docs);
          setTotalDocs(isDateFiltered ? docs.length : (response?.data?.totalDocs || 0));
          setTotalPages(response?.data?.totalPages || 0);
        } else {
          setOrdersList([]);
          setTotalDocs(0);
          setTotalPages(0);
        }
      })
      .catch((err) => {
        console.error("Error fetching orders:", err);
        setOrdersList([]);
        setTotalDocs(0);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, sizePerPage, search, selectedStatus, isDateFiltered, dateRange, userState?.token]);

  const handleSyncRazorpay = async (order) => {
    try {
      setSyncingId(order._id);
      await handleAddToken(userState?.token);
      const res = await syncOrderRazorpayAPI(order._id);
      if (res?.status && res?.data?.paymentStatus === "paid") {
        toast.success(`Payment verified! Order #${order.orderId} marked as Paid.`);
        ordersGetFunction();
      } else {
        toast.info(res?.message || "No captured payment found on Razorpay.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to sync with Razorpay.");
    } finally {
      setSyncingId(null);
    }
  };

  /* COLUMNS */
  const columns = [
    {
      name: "Order ID",
      grow: 1,
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          #{row.orderId}
        </span>
      ),
    },
    {
      name: "Customer Name",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.personalDetails?.firstName} {row?.personalDetails?.lastName}
        </span>
      ),
    },
    {
      name: "Customer Email",
      width: "25vh",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.personalDetails?.email}
        </span>
      ),
    },
    {
      name: "Customer Phone",
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.personalDetails?.phoneNumber}
        </span>
      ),
    },
    {
      name: "Purchase Date",
      grow: 2,
      selector: (row) => (
        <span className="text-sm font-proxima-semibold text-black text-opacity-60">
          {row?.createdAt
            ? moment(row.createdAt).format("MMM DD, YYYY hh:mm A")
            : "-"}
        </span>
      ),
    },
    {
      name: "Amount",
      selector: (row) => (
        <span className="text-sm font-proxima-bold text-black">
          {SET_FORMAT(row.payAmount || row.amount || 0)}
        </span>
      ),
    },
    {
      name: "Payment Status",
      selector: (row) => {
        const pStatus = (row.paymentStatus || "").toLowerCase();
        if (pStatus === "paid" || pStatus === "completed" || pStatus === "success") {
          return (
            <span className="inline-flex items-center text-xs font-proxima-semibold px-2.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 capitalize">
              Paid
            </span>
          );
        }
        if (pStatus === "pending") {
          return (
            <span className="inline-flex items-center text-xs font-proxima-semibold px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 capitalize">
              Pending
            </span>
          );
        }
        return (
          <span className="inline-flex items-center text-xs font-proxima-semibold px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 capitalize">
            {row.paymentStatus || "Failed"}
          </span>
        );
      },
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="flex items-center gap-1">
          <Tooltip content="View Order Details" className="text-sm font-proxima-semibold">
            <Button
              onClick={() => {
                navigate("/order/" + row?._id);
              }}
              className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5"
            >
              <IoEyeSharp className="w-5 h-5 text-greyText" />
            </Button>
          </Tooltip>
          <Tooltip content="Sync with Razorpay" className="text-sm font-proxima-semibold">
            <Button
              disabled={syncingId === row._id}
              onClick={() => handleSyncRazorpay(row)}
              className="p-2 rounded-full shadow-none hover:shadow-none bg-transparent hover:bg-black hover:bg-opacity-5 text-greyText hover:text-black transition-all"
            >
              <IoSyncOutline
                className={`w-5 h-5 ${
                  syncingId === row._id ? "animate-spin text-logoYellow" : ""
                }`}
              />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];


  /* USE-EFFECTS */
  useEffect(() => {
    ordersGetFunction();
  }, [ordersGetFunction]);

  return (
    <section className="px-0 py-2 xl:p-10 lg:p-10 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Orders | CIDA Perfumes</title>
      </Helmet>

      <div className="container-full-width">
        <div className="block xl:flex lg:flex items-center justify-between">
          {/* HEADING */}
          <div>
            <h1 className="text-3xl font-proxima-bold text-black tracking-tight">
              Customer Orders
            </h1>
            <h2 className="text-base xl:text-lg lg:text-lg font-proxima-regular text-greyText tracking-tight">
              Manage the orders placed by the customers from your website
            </h2>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="block xl:flex lg:flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            {/* SEARCHBAR */}
            <div className="w-full xl:w-searchBar lg:w-searchBar">
              <InputField
                type="text"
                placeholder="Search your order..."
                name="search"
                id="search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {/* RANGEPICKER */}
            <div ref={domNode} className="relative mt-2 xl:mt-0 lg:mt-0 w-full xl:w-auto lg:w-auto">
              <div
                className="w-full xl:w-rangePicker lg:w-rangePicker flex items-center justify-between gap-2 border border-authBorder rounded-lg py-3 px-4 bg-white cursor-pointer select-none"
                onClick={handleOpenDatePicker}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <FaCalendar className="w-4 h-4 text-greyText flex-shrink-0" />
                  <input
                    type="text"
                    readOnly
                    name="dateRange"
                    placeholder="Filter by date range"
                    value={
                      isDateFiltered
                        ? `${moment(dateRange[0].startDate).format("DD/MM/YYYY")} - ${moment(dateRange[0].endDate).format("DD/MM/YYYY")}`
                        : ""
                    }
                    className="w-full bg-white outline-none font-proxima-semibold text-sm placeholder:font-proxima-regular placeholder:text-greyText text-black cursor-pointer truncate"
                  />
                </div>
                {isDateFiltered && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearDate();
                    }}
                    className="p-1 rounded-full hover:bg-black/5 text-greyText hover:text-black flex-shrink-0"
                    title="Clear date filter"
                  >
                    <IoClose className="w-4 h-4" />
                  </button>
                )}
              </div>
              {datepicker && (
                <div className="absolute top-12 right-0 transition-all duration-300 ease-linear z-50 bg-white rounded-xl shadow-2xl border border-authBorder overflow-hidden">
                  <DateRangePicker
                    ranges={tempRange}
                    dragSelectionEnabled
                    showPreview
                    onChange={(item) => {
                      setTempRange([item.selection]);
                    }}
                    className="font-proxima-regular cursor-pointer"
                  />
                  <div className="flex items-center justify-between p-3 bg-gray-50 border-t border-authBorder">
                    <button
                      type="button"
                      onClick={handleClearDate}
                      className="text-xs font-proxima-semibold text-gray-600 hover:text-black px-3 py-1.5 rounded hover:bg-gray-200 transition-colors"
                    >
                      Clear
                    </button>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={closeDatePicker}
                        className="text-xs font-proxima-semibold text-gray-500 hover:text-black px-3 py-1.5 rounded hover:bg-gray-200 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleApplyDate}
                        className="text-xs font-proxima-semibold text-white bg-black hover:bg-gray-800 px-4 py-1.5 rounded transition-colors shadow-sm"
                      >
                        Apply Filter
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ORDERS TABLE */}
        <div className="mt-5">
          <DataTable
            className="data-table"
            data={ordersList}
            columns={columns}
            responsive
            progressPending={loading}
            progressComponent={
              <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
                <p className="text-sm font-proxima-semibold text-black text-center">
                  Loading orders...
                </p>
              </div>
            }
            pagination
            paginationServer
            paginationTotalRows={totalDocs}
            paginationPerPage={sizePerPage}
            paginationDefaultPage={page}
            onChangePage={(newPage) => {
              setPage(newPage);
            }}
            onChangeRowsPerPage={(newPerPage, newPage) => {
              setSizePerPage(newPerPage);
              setPage(newPage);
            }}
            noDataComponent={
              <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5">
                <p className="text-sm font-proxima-semibold text-black text-center">
                  There are no orders to display.
                </p>
              </div>
            }
          />
        </div>
      </div>
    </section>
  );
};

export default Orders;
